import { Injectable, inject, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { AuthStorageService } from './auth-storage.service';
import { MessageNotificationService } from './message-notification.service';

@Injectable({
  providedIn: 'root',
})
export class AuthFeaturesService implements OnDestroy {
  private readonly authStorageService = inject(AuthStorageService);
  private readonly notificationService = inject(MessageNotificationService);

  private readonly ngUnsubscribe = new Subject<void>();
  private notificationSubscription?: Subscription;

  /**
   * Initializes all features that depend on authentication state
   */
  initialize(): void {
    this.setupAuthListeners();
  }

  /**
   * Sets up listeners for authentication state changes
   */
  private setupAuthListeners(): void {
    this.authStorageService.isAuthenticated$
      .pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
      .subscribe(isAuthenticated => {
        this.cleanupNotifications();

        if (isAuthenticated) {
          this.setupNotifications();
        }
      });
  }

  /**
   * Sets up notification subscriptions for the authenticated user
   */
  private setupNotifications(): void {
    const userId = this.authStorageService.getUserId();
    if (!userId) {
      console.warn('User ID not found, notifications will not be set up.');
      return;
    }

    this.notificationService.requestPermission().subscribe({
      next: token => {
        this.authStorageService.setNotificationToken(token);
      },
      error: err => console.error('Error requesting notification permission', err),
    });
  }

  /**
   * Cleans up notification subscriptions
   */
  private cleanupNotifications(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
      this.notificationSubscription = undefined;
    }
  }

  ngOnDestroy(): void {
    this.cleanupNotifications();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
